import type { FC, KeyboardEventHandler } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// interstate components
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import { Typography } from '@interstate/components/Typography';

// CSS style components
import { kebabCase } from '@makemydeal/dr-common-utils';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    inputFieldRightAlign,
    RollToOptionsTabbedContentWrapper,
    StyledRollToOptionsBannerRegion,
    StyledRollToOptionsTextInput
} from '../RollToOptions.interstate.style';
import DealPulseToolTipRenderer from './DealPulseToolTipRenderer';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as rollToSelectors from '@makemydeal/dr-dash-store/selectors/rollToSelectors';
import RollToCannotApplyError from '../RollToCannotApplyError';

type DisplayProps = {
    option: string;
    currentValue: number;
    targetValue?: number;
    instruction: string;
    showDealPulseTooltip?: boolean;
};

type RollToOptionTargetProps = {
    displayProps: DisplayProps;
    dispatchAction: (value: number) => void;
    clearAction?: () => void;
};

const RollToOptionTarget: FC<RollToOptionTargetProps> = ({ displayProps, dispatchAction, clearAction }) => {
    const { option, currentValue, targetValue, instruction, showDealPulseTooltip } = displayProps;
    const dispatch = useDispatch();
    const hasPersistedTarget = Boolean(targetValue && targetValue > 0);
    const [targetInputValue, setTargetInputValue] = useState<string>(hasPersistedTarget ? String(targetValue) : '');
    const [targetInputFieldWasDirtied, setTargetInputFieldWasDirtied] = useState<boolean>(hasPersistedTarget);
    const errors = useSelector(rollToSelectors.getRollToErrors);
    const hasRollToErrors = useSelector(rollToSelectors.hasRollToErrors);

    useEffect(() => {
        const hasPersistedTargetValue = Boolean(targetValue && targetValue > 0);

        setTargetInputValue(hasPersistedTargetValue ? String(targetValue) : '');
        setTargetInputFieldWasDirtied(hasPersistedTargetValue);
    }, [targetValue]);

    const handleSubmitTargetInput = useCallback(() => {
        const targetPayment = Number(targetInputValue);

        if (!targetPayment || isNaN(targetPayment)) {
            return;
        }
        setTargetInputFieldWasDirtied(true);
        dispatch(dispatchAction(targetPayment));
    }, [dispatch, dispatchAction, targetInputValue]);

    const handleClearTargetInput = () => {
        setTargetInputValue('');
        setTargetInputFieldWasDirtied(false);
        if (clearAction) {
            dispatch(clearAction());
        }
    };

    const handleChangeTargetPayment: InterstateOnChangeCallback<string> = useCallback(
        (event) => {
            const value = event.target.value;

            setTargetInputValue(value);
            if (targetInputFieldWasDirtied && value !== targetInputValue) {
                setTargetInputFieldWasDirtied(false);
            }
        },
        [targetInputFieldWasDirtied, targetInputValue]
    );

    const handleKeyDownTargetPayment: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (document.activeElement !== e.target) {
            return;
        }
        const key = e.key;
        const keyCode = e.keyCode;

        if (key === 'Enter' || keyCode === 13) {
            handleSubmitTargetInput();
        }
    };

    return (
        <RollToOptionsTabbedContentWrapper id="roll-to-options-tab-content" data-testid="roll-to-options-tab-content">
            {hasRollToErrors && (
                <Box sx={{ marginBottom: '1rem' }}>
                    <RollToCannotApplyError errors={errors} />
                </Box>
            )}
            <StyledRollToOptionsBannerRegion id="roll-to-options-banner-region" data-testid="roll-to-options-banner-region">
                <Typography variant="h3" tag="div">
                    {instruction}
                </Typography>

                <Box display="flex" alignItems="center" flexDirection="row" justifyContent="space-between" flexWrap="nowrap">
                    <div>
                        <span id={`current-payment-label`}>{`Current ${option}`}</span>
                        <span id={`current-payment-value`} data-testid={`current-payment-value-${kebabCase(option)}`}>
                            {formatDollarsAndCents(currentValue)}
                        </span>
                    </div>

                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <span id="target-payment-input-label">{`Target ${option}`}</span>
                        <span>{showDealPulseTooltip && <DealPulseToolTipRenderer toolTipContentPlaceHolder={option} />}</span>
                        <StyledRollToOptionsTextInput>
                            <NumericInput
                                displayLabel={false}
                                id="target-payment-text-input"
                                data-testid="target-payment-text-input"
                                inputPrefix="$"
                                value={targetInputValue}
                                name="roll-to-options-text-input"
                                onChange={handleChangeTargetPayment}
                                onKeyDown={handleKeyDownTargetPayment}
                                sx={inputFieldRightAlign}
                                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            />
                        </StyledRollToOptionsTextInput>
                    </Box>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <div>
                        {targetInputFieldWasDirtied ? (
                            <Button
                                data-testid="btn-target-payment-clear-input"
                                buttonStyle="secondary"
                                size="small"
                                onClick={handleClearTargetInput}
                            >
                                Clear
                            </Button>
                        ) : (
                            <Button
                                data-testid="btn-target-payment-set-target"
                                buttonStyle="secondary"
                                size="small"
                                onClick={handleSubmitTargetInput}
                                disabled={!targetInputFieldWasDirtied && (!targetInputValue || Number(targetInputValue) <= 0)}
                            >
                                Set Target
                            </Button>
                        )}
                    </div>
                </Box>
            </StyledRollToOptionsBannerRegion>
        </RollToOptionsTabbedContentWrapper>
    );
};

export default RollToOptionTarget;
